import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Copy, RefreshCw, Download, Upload, Eye, EyeOff, Wand2, Type, Hash, Trash2 } from 'lucide-react';

interface CaseOption {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  transform: (text: string) => string;
}

interface HistoryItem {
  id: string;
  input: string;
  output: string;
  caseType: string;
  timestamp: Date;
}

const CaseConverter: React.FC = () => {
  const [inputText, setInputText] = useState<string>('');
  const [selectedCase, setSelectedCase] = useState<string>('uppercase');
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [wordCount, setWordCount] = useState<number>(0);
  const [charCount, setCharCount] = useState<number>(0);
  const [copied, setCopied] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const caseOptions: CaseOption[] = [
    {
      id: 'uppercase',
      name: 'UPPERCASE',
      description: 'Converts all text to uppercase',
      icon: <Type className="w-4 h-4" />,
      transform: (text: string) => text.toUpperCase()
    },
    {
      id: 'lowercase',
      name: 'lowercase',
      description: 'Converts all text to lowercase',
      icon: <Type className="w-4 h-4" />,
      transform: (text: string) => text.toLowerCase()
    },
    {
      id: 'capitalize',
      name: 'Capitalize First',
      description: 'Capitalizes the first letter of each word',
      icon: <Type className="w-4 h-4" />,
      transform: (text: string) => text.replace(/\b\w/g, l => l.toUpperCase())
    },
    {
      id: 'sentence',
      name: 'Sentence case',
      description: 'Capitalizes the first letter of each sentence',
      icon: <Type className="w-4 h-4" />,
      transform: (text: string) => text.toLowerCase().replace(/(^\s*\w|[.!?]\s*\w)/g, c => c.toUpperCase())
    },
    {
      id: 'camelCase',
      name: 'camelCase',
      description: 'Converts to camelCase format',
      icon: <Hash className="w-4 h-4" />,
      transform: (text: string) => text.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => 
        index === 0 ? word.toLowerCase() : word.toUpperCase()).replace(/\s+/g, '')
    },
    {
      id: 'pascalCase',
      name: 'PascalCase',
      description: 'Converts to PascalCase format',
      icon: <Hash className="w-4 h-4" />,
      transform: (text: string) => text.replace(/(?:^\w|[A-Z]|\b\w)/g, word => 
        word.toUpperCase()).replace(/\s+/g, '')
    },
    {
      id: 'snake_case',
      name: 'snake_case',
      description: 'Converts to snake_case format',
      icon: <Hash className="w-4 h-4" />,
      transform: (text: string) => text.toLowerCase().replace(/\s+/g, '_').replace(/[^\w_]/g, '')
    },
    {
      id: 'kebab-case',
      name: 'kebab-case',
      description: 'Converts to kebab-case format',
      icon: <Hash className="w-4 h-4" />,
      transform: (text: string) => text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')
    },
    {
      id: 'alternating',
      name: 'aLtErNaTiNg',
      description: 'Alternates between upper and lowercase',
      icon: <Wand2 className="w-4 h-4" />,
      transform: (text: string) => text.split('').map((char, index) => 
        index % 2 === 0 ? char.toLowerCase() : char.toUpperCase()).join('')
    },
    {
      id: 'inverse',
      name: 'iNVERSE cASE',
      description: 'Inverts the case of each character',
      icon: <RefreshCw className="w-4 h-4" />,
      transform: (text: string) => text.split('').map(char => 
        char === char.toUpperCase() ? char.toLowerCase() : char.toUpperCase()).join('')
    }
  ];

  const convertedText = useCallback(() => {
    const option = caseOptions.find(opt => opt.id === selectedCase);
    return option ? option.transform(inputText) : inputText;
  }, [inputText, selectedCase, caseOptions]);

  useEffect(() => {
    setWordCount(inputText.trim() === '' ? 0 : inputText.trim().split(/\s+/).length);
    setCharCount(inputText.length);
  }, [inputText]);

  const handleCopy = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(''), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleConvert = () => {
    if (inputText.trim()) {
      const converted = convertedText();
      const newHistoryItem: HistoryItem = {
        id: Date.now().toString(),
        input: inputText,
        output: converted,
        caseType: caseOptions.find(opt => opt.id === selectedCase)?.name || selectedCase,
        timestamp: new Date()
      };
      setHistory(prev => [newHistoryItem, ...prev.slice(0, 9)]); // Keep last 10 items
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'text/plain') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputText(content);
      };
      reader.readAsText(file);
    }
  };

  const handleDownload = () => {
    const converted = convertedText();
    const blob = new Blob([converted], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `converted-text-${selectedCase}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearAll = () => {
    setInputText('');
    setHistory([]);
  };

  const loadFromHistory = (item: HistoryItem) => {
    setInputText(item.input);
    const caseOption = caseOptions.find(opt => opt.name === item.caseType);
    if (caseOption) {
      setSelectedCase(caseOption.id);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl mb-4 shadow-lg">
            <Type className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
            Advanced Case Converter
          </h1>
          <p className="text-gray-600 text-lg">Transform your text with powerful case conversion tools</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Converter */}
          <div className="lg:col-span-2 space-y-6">
            {/* Input Section */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                    <Type className="w-5 h-5 mr-2 text-indigo-500" />
                    Input Text
                  </h2>
                  <div className="flex items-center space-x-2">
                    <input
                      type="file"
                      accept=".txt"
                      onChange={handleFileUpload}
                      ref={fileInputRef}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      Upload
                    </button>
                    <button
                      onClick={clearAll}
                      className="flex items-center px-3 py-1.5 text-sm bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Clear
                    </button>
                  </div>
                </div>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Enter your text here to convert..."
                  className="w-full h-32 p-4 border border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-gray-700"
                />
                <div className="flex justify-between items-center mt-3 text-sm text-gray-500">
                  <span>Words: {wordCount} | Characters: {charCount}</span>
                  <button
                    onClick={() => handleCopy(inputText, 'input')}
                    className="flex items-center px-2 py-1 hover:bg-gray-100 rounded transition-colors"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    {copied === 'input' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Case Options */}
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Conversion Options</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {caseOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setSelectedCase(option.id)}
                      className={`p-3 rounded-xl border-2 transition-all text-left group ${
                        selectedCase === option.id
                          ? 'border-indigo-500 bg-indigo-50 shadow-md'
                          : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center mb-1">
                        <span className={`mr-2 ${selectedCase === option.id ? 'text-indigo-600' : 'text-gray-400'}`}>
                          {option.icon}
                        </span>
                        <span className={`font-medium text-sm ${
                          selectedCase === option.id ? 'text-indigo-900' : 'text-gray-700'
                        }`}>
                          {option.name}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">{option.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Output Section */}
            
          </div>

          {/* History Sidebar */}
          <div className="lg:col-span-1">
           <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleConvert}
                      className="flex items-center px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Convert
                    </button>
                    <button
                      onClick={handleDownload}
                      className="flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </button>
                  </div>
                </div>
                <div className="relative">
                  <textarea
                    value={convertedText()}
                    readOnly
                    className="w-full h-32 p-4 border border-gray-200 rounded-xl resize-none bg-gray-50 text-gray-700 focus:ring-2 focus:ring-purple-500"
                  />
                  <button
                    onClick={() => handleCopy(convertedText(), 'output')}
                    className="absolute top-3 right-3 flex items-center px-3 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg transition-colors text-sm shadow-sm"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    {copied === 'output' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseConverter;